import { useTranslation } from 'react-i18next';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs';

import { useExperimentKpis } from '../../hooks/use-experiments';
import type { PerformanceViewMode } from '../../hooks/use-performance-view-mode';
import { useExperimentHealthPrefetch } from '../../hooks/use-prefetch-experiment-health';
import type { AnalyticsGrain, AnalyticsSensitivity } from '../../types/analytics-controls.types';

import { ExperimentArmComparison } from './experiment-arm-comparison';
import { ExperimentCategoryPerformancePanel } from './experiment-category-performance-panel';
import { ExperimentDailyLiftTable } from './experiment-daily-lift-table';
import type { ExperimentDetailsSubTab } from './experiment-details-sub-tab';
import { ExperimentHealthTab } from './experiment-health-tab';
import { ExperimentKpiCards } from './experiment-kpi-cards';
import { ExperimentPerformancePanel } from './experiment-performance-panel';
import { ExperimentRoasComparison } from './experiment-roas-comparison';
import { RevenueSourceBanner } from './revenue-source-banner';

interface Props {
  experimentId: string;
  /** Controlled active sub-tab — lifted to the page so the chatbot can mirror it. */
  subTab: ExperimentDetailsSubTab;
  onSubTabChange: (next: ExperimentDetailsSubTab) => void;
  /** Whether to render the "View as" dropdown (true when category_performance is enabled). */
  showViewDropdown: boolean;
  /** Current "View as" selection — controls which content renders in the Performance tab. */
  viewMode: PerformanceViewMode;
  /** Called when the user changes the "View as" dropdown. */
  onViewModeChange: (mode: PerformanceViewMode) => void;
  /** Fixed analytics grain (Performance panel + CSV export); no longer user-configurable. */
  grain: AnalyticsGrain;
  /** Fixed outlier sensitivity (Performance / Health / Chatbot); no longer user-configurable. */
  sensitivity: AnalyticsSensitivity;
  /** Encoded `?segment=` product filter (see `encodeSegmentFilter`); `undefined` = unscoped, full-cohort data. */
  segment?: string | undefined;
}

/** Details section with Performance and Health sub-tabs plus an optional "View as" dropdown. */
export function ExperimentDetailsTab({
  experimentId,
  subTab,
  onSubTabChange,
  showViewDropdown,
  viewMode,
  onViewModeChange,
  grain,
  sensitivity,
  segment,
}: Props) {
  const { t } = useTranslation('experiments');

  const {
    data: kpis,
    isLoading: kpisLoading,
    isError: kpisError,
  } = useExperimentKpis(experimentId, segment);

  // Prefetch the Health sub-tab so switching to it is instant: warm it eagerly
  // in the background while Performance is showing, and again on hover/focus of
  // the Health tab as an immediate-intent backstop.
  const prefetchHealth = useExperimentHealthPrefetch(
    experimentId,
    sensitivity,
    subTab === 'performance',
  );

  // Count KPI cards sit at the top of the Performance and Health tab bodies.
  // The Category panel owns its own lift-KPI row so they're omitted there.
  const countKpiCards = (
    <ExperimentKpiCards kpis={kpis} isLoading={kpisLoading} isError={kpisError} />
  );

  return (
    <div className="space-y-6">
      <Tabs
        value={subTab}
        onValueChange={(value) => {
          onSubTabChange(value as ExperimentDetailsSubTab);
        }}
        className="space-y-6"
      >
        {/* Tab row: Performance | Health on left, optional "View as" dropdown on right */}
        <div className="flex items-center justify-between">
          <TabsList className="bg-white">
            <TabsTrigger value="performance" className="w-36 cursor-pointer">
              {t('detail.subtabs.performance')}
            </TabsTrigger>
            <TabsTrigger
              value="health"
              className="w-36 cursor-pointer"
              onMouseEnter={prefetchHealth}
              onFocus={prefetchHealth}
            >
              {t('detail.subtabs.health')}
            </TabsTrigger>
          </TabsList>

          {showViewDropdown && subTab === 'performance' && (
            <div className="flex items-center gap-2">
              <span className="text-foreground text-sm font-semibold">{t('detail.viewAs')}</span>
              <Select
                value={viewMode}
                onValueChange={(v) => {
                  onViewModeChange(v as PerformanceViewMode);
                }}
              >
                <SelectTrigger className="w-56 cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="category" className="cursor-pointer">
                    {t('detail.viewMode.category')}
                  </SelectItem>
                  <SelectItem value="default" className="cursor-pointer">
                    {t('detail.viewMode.default')}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* One fallback note per page — the source is decided once per experiment. */}
        <RevenueSourceBanner source={kpis?.revenueSource} />

        <TabsContent value="performance" className="mt-0 space-y-6">
          {viewMode === 'category' ? (
            // Category based dashboard — panel owns its own KPI lift row.
            <ExperimentCategoryPerformancePanel experimentId={experimentId} segment={segment} />
          ) : (
            <>
              {countKpiCards}
              <ExperimentPerformancePanel
                experimentId={experimentId}
                grain={grain}
                segment={segment}
              />
              <ExperimentArmComparison experimentId={experimentId} segment={segment} />
              <ExperimentRoasComparison experimentId={experimentId} segment={segment} />
              <ExperimentDailyLiftTable experimentId={experimentId} segment={segment} />
            </>
          )}
        </TabsContent>

        <TabsContent value="health" className="mt-0 space-y-6">
          {countKpiCards}
          <ExperimentHealthTab experimentId={experimentId} sensitivity={sensitivity} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
